import { useState } from "react";
import { useMutation, useAction } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { toConvexId } from "@/types/convex";

interface CreateParentUserData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  password: string; // Required for parent user creation
  childFirstName: string;
  childLastName: string;
  childEmail?: string;
  childPhone?: string;
}

interface CreationResult {
  deviceId: string;
  parentUserId: string;
  childUserId: string;
  groupId: string;
}

export const useParentUserCreation = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>("");
  const { toast } = useToast();
  
  const createUserMutation = useMutation(api.users.create.user);
  const createManagerAction = useAction(api.users.create.manager);
  const createGroupMutation = useMutation(api.groups.create.group);
  const createDeviceRegistrationMutation = useMutation(api.devices.registrations.create);

  const createParentUser = async (userData: CreateParentUserData): Promise<CreationResult> => {
    console.log("🚀 Starting parent user creation flow", userData);
    setIsLoading(true);
    
    try {
      // Step 1: Create parent user using manager API
      setCurrentStep("Creating parent profile...");
      console.log("📝 Step 1: Creating parent user with manager API");
      
      const parentUserId = await createManagerAction({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        phone: userData.phone,
        password: userData.password,
        identity: "parent",
      });
      console.log("✅ Parent user created:", parentUserId);

      // Step 2: Create the child user - WAIT for parent to be ready
      setCurrentStep("Creating child profile...");
      console.log("👶 Step 2: Creating child user");
      
      const childEmail = userData.childEmail || `${userData.childFirstName.toLowerCase()}.${userData.childLastName.toLowerCase()}@temp.com`;
      console.log("👶 Child user data:", {
        firstName: userData.childFirstName,
        lastName: userData.childLastName,
        email: childEmail,
        phone: userData.childPhone,
      });
      
      const childUserId = await createUserMutation({
        firstName: userData.childFirstName,
        lastName: userData.childLastName,
        email: childEmail,
        phone: userData.childPhone,
        identity: "child",
      });
      console.log("✅ Child user created:", childUserId);

      // Step 3: Create a family group - WAIT for child to be created
      setCurrentStep("Setting up family group...");
      console.log("👨‍👩‍👧‍👦 Step 3: Creating family group");
      
        const groupData = {
          name: `${userData.firstName} family`,
          type: "family" as const,
          user: parentUserId,
        };
      console.log("👨‍👩‍👧‍👦 Group data:", groupData);
      
      const groupId = await createGroupMutation(groupData);
      console.log("✅ Family group created:", groupId);

      // Step 4: Register a device - WAIT for group to be created
      setCurrentStep("Registering device...");
      console.log("📱 Step 4: Registering device");
      
      const deviceData = {
        group: groupId,
        user: childUserId,
        adBlock: false, // Default value
        restrictions: {}, // Default empty restrictions
      };
      console.log("📱 Device registration data:", deviceData);
      
      const deviceId = await createDeviceRegistrationMutation(deviceData);
      console.log("✅ Device registered:", deviceId);

      const result = {
        deviceId,
        parentUserId,
        childUserId,
        groupId,
      };
      console.log("🎉 Full creation result:", result);

      toast({
        title: "Success!",
        description: "Parent account, child profile, family group, and device have been created successfully.",
      });

      return result;

    } catch (error) {
      console.error("❌ Failed to create parent user:", error);
      console.error("❌ Current step when failed:", currentStep);
      console.error("❌ Full error details:", {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      toast({
        title: "Error",
        description: `Failed during: ${currentStep}. Please try again.`,
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsLoading(false);
      setCurrentStep("");
      console.log("🏁 Parent creation flow completed");
    }
  };

  return {
    createParentUser,
    isLoading,
    currentStep,
  };
};